import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { css, Theme } from '@emotion/react';
import { rem } from 'polished';
import { colors, mixins, spaces, text } from '../../core/styles/theme/default';
import { useWindowSize } from '../../core/utils/Hooks';
import {
  BaseAccordion,
  BaseAccordionProps,
  BaseAccordionItem,
  BaseAccordionItemProps,
  BaseAccordionItemContent,
  BaseAccordionItemContentProps,
  BaseAccordionItemTrigger,
  BaseAccordionItemTriggerProps,
} from '../BaseAccordion';
import { IconSVG } from '../../primitives';
import { ToggleIcon, ToggleText } from './partials';
import { IconInterface, TypographyInterface } from '../../types';
import { Spacer } from '../../helpers';

const StyledAccordionItem = styled(BaseAccordionItem)(
  ({ theme }: { theme: Theme }) => css`
    position: relative;
    border-bottom: ${rem(1)} solid ${colors.grey300};

    ${theme?.stylesExtension?.accordion?.accordionItem}
  `,
);

const StyledAccordionItemTrigger = styled(BaseAccordionItemTrigger)`
  display: flex;
  width: 100%;
  color: ${colors.primary};
  cursor: pointer;

  ${mixins.responsiveStyles({
    'padding-top': { zero: rem(spaces.delta), medium: rem(spaces.epsilon) },
    'padding-bottom': { zero: rem(spaces.delta), medium: rem(spaces.epsilon) },
  })}
`;

const StyledIcon = styled(IconSVG)(
  () => css`
    margin-right: ${rem(spaces.gamma)};
    path {
      stroke: black;
    }
  `,
);

const StyledImg = styled.img(
  ({
    titleLineHeight,
    theme,
  }: {
    titleLineHeight?: string;
    theme?: Theme;
  }) => css`
    ${mixins.responsiveStyles({
      width: { zero: rem(72), large: rem(144) },
      height: { zero: rem(72), large: rem(144) },
    })}
    border-radius: ${rem(4)};
    margin-right: ${rem(spaces.gamma)};

    ${titleLineHeight &&
    css`
      line-height: ${rem(titleLineHeight)};
    `}

    ${theme?.stylesExtension?.accordion?.img}
  `,
);

const StyledCounter = styled.div(
  ({
    counter,
    titleLineHeight,
    bgColor,
    theme,
  }: {
    counter?: number;
    titleLineHeight?: string;
    bgColor?: string;
    theme?: Theme;
  }) => css`
    padding-right: ${rem(spaces.delta)};
    width: ${rem(48)};
    height: ${rem(33)};
    box-sizing: content-box;
    position: relative;
    flex-shrink: 0;

    ${titleLineHeight &&
    css`
      line-height: ${rem(titleLineHeight)};
    `}

    &&:after {
      content: '${counter}';
      width: ${rem(48)};
      height: ${rem(48)};
      border: 2px solid #faa829;
      border-radius: 100%;
      color: #faa829;
      font-size: ${rem(24)};
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      box-sizing: border-box;

      ${bgColor &&
      css`
        font-weight: 900;
        border: none;
        color: white;
        background: ${bgColor};
      `}
    }

    ${theme?.stylesExtension?.accordion?.counter}
  `,
);

const StyledTriggerChildren = styled.div`
  flex: 1;
  align-self: center;
`;

const StyledTitle = styled.div(
  ({
    titleStyle,
    theme,
  }: {
    titleStyle?: TypographyInterface;
    theme?: Theme;
  }) => css`
    ${titleStyle && text[titleStyle]}
    font-weight: 400;

    ${mixins.responsiveStyles({
      'font-size': { zero: rem(20), medium: rem(22), large: rem(24) },
    })}

    ${theme?.stylesExtension?.accordion?.title}
  `,
);

const StyledDescription = styled.div`
  ${mixins.responsiveStyles({
    'padding-top': { zero: rem(spaces.alpha), medium: rem(6) },
    'padding-bottom': { zero: rem(spaces.alpha), medium: rem(6) },
  })}
  color: ${colors.grey600};
`;

const StyledAccordionItemContent = styled(BaseAccordionItemContent)``;

const Accordion = ({ children, ...rest }: AccordionProps) => {
  return <BaseAccordion {...rest}>{children}</BaseAccordion>;
};

const AccordionItem = ({ children, ...rest }: AccordionItemProps) => {
  return <StyledAccordionItem {...rest}>{children}</StyledAccordionItem>;
};

const AccordionItemTrigger = ({
  children,
  icon,
  src,
  iconSize = 24,
  counter,
  titleStyle = 'h3',
  description,
  textCollapsed,
  textExpanded,
  counterBackground,
  ...rest
}: AccordionItemTriggerProps) => {
  const [titleLineHeight, setTitleLineHeight] = useState<string>();
  const titleRef = useRef(null);
  const { width: windowWidth } = useWindowSize();

  useEffect(() => {
    // no else path needed - but jest expect it - so we ignore it here
    /* istanbul ignore else */
    if (titleRef.current) {
      const { current } = titleRef;
      const lineHeight = window
        .getComputedStyle(current)
        .getPropertyValue('line-height');

      // jest does not output the line-height in px - so we check for a px value here, but ignore it for jest
      /* istanbul ignore next */
      if (lineHeight.includes('px') && titleLineHeight !== lineHeight) {
        setTitleLineHeight(lineHeight);
      }
    }
    // ignore titleRef & titleLineHeight in dependency array to prevent unneeded renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowWidth]);

  return (
    <StyledAccordionItemTrigger {...rest}>
      {!counter && icon && <StyledIcon iconName={icon} iconSize={iconSize} />}
      {!counter && src && (
        <StyledImg src={src} alt="" titleLineHeight={titleLineHeight} />
      )}
      {counter && (
        <StyledCounter
          counter={counter}
          titleLineHeight={titleLineHeight}
          bgColor={counterBackground}
        />
      )}
      <StyledTriggerChildren>
        <StyledTitle titleStyle={titleStyle} ref={titleRef}>
          {children}
        </StyledTitle>
        {description && (
          <StyledDescription
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
      </StyledTriggerChildren>
      <ToggleText
        textCollapsed={textCollapsed}
        textExpanded={textExpanded}
        titleLineHeight={titleLineHeight}
      />
      <ToggleIcon
        titleLineHeight={titleLineHeight}
        color={counterBackground ? colors.primary : undefined}
      />
    </StyledAccordionItemTrigger>
  );
};

const AccordionItemContent = ({
  children,
  ...rest
}: AccordionItemContentProps) => {
  return (
    <StyledAccordionItemContent {...rest}>
      {children}
      <Spacer space="hecto" />
    </StyledAccordionItemContent>
  );
};

export type AccordionProps = BaseAccordionProps;

export type AccordionItemProps = BaseAccordionItemProps;

export type AccordionItemTriggerProps = BaseAccordionItemTriggerProps & {
  /** Name of the Icon which will be shown before the Title */
  icon?: IconInterface;
  /** Size of the Icon */
  iconSize?: number;
  /** Activate counter instead of icon */
  counter?: number;
  /** Background color of counter */
  counterBackground?: string;
  /** Style of the Title */
  titleStyle?: TypographyInterface;
  /** Description below the Title */
  description?: string;
  /** Text beside the ToggleIcon when collapsed */
  textCollapsed?: string;
  /** Text beside the ToggleIcon when expanded */
  textExpanded?: string;
  /** Img trigger */
  src?: string;
};

export type AccordionItemContentProps = BaseAccordionItemContentProps;

export { Accordion, AccordionItem, AccordionItemTrigger, AccordionItemContent };
